const API_BASE_URL = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '')
const CHECK_TOKEN_PATH = '/api/member/portfolio/checkToken'
const TOKEN_STORAGE_KEY = 'portfolioAccessToken'
const POLL_MS = 7000
const REQUEST_TIMEOUT_MS = 6000
const TOKEN_PATTERN = /^[A-HJKMNP-Z2-9]{16}$/

const normalizeToken = value => String(value || '').toUpperCase().replace(/\s+/g, '')

const clearBody = () => {
  document.body.innerHTML = ''
}

const renderShell = content => {
  clearBody()
  document.body.insertAdjacentHTML('afterbegin', `
    <style id="portfolio-access-style">
      * { box-sizing: border-box; }
      html, body { min-height: 100%; margin: 0; }
      .portfolio-access-page {
        min-height: 100vh;
        padding: 24px;
        display: grid;
        place-items: center;
        background:
          radial-gradient(circle at 18% 18%, rgba(255,255,255,.09), transparent 26%),
          radial-gradient(circle at 82% 76%, rgba(193,164,93,.16), transparent 32%),
          #101010;
        color: #fff;
        font-family: Arial, Helvetica, sans-serif;
      }
      .portfolio-access-card {
        width: min(100%, 480px);
        padding: 48px 44px;
        border: 1px solid rgba(218,188,112,.32);
        border-radius: 28px;
        background: rgba(25,25,25,.94);
        box-shadow: 0 30px 80px rgba(0,0,0,.35);
      }
      .portfolio-access-center { text-align: center; }
      .portfolio-access-brand {
        color: #d9bd70;
        font: 800 48px/1 Georgia, 'Times New Roman', serif;
        letter-spacing: 2px;
      }
      .portfolio-access-kicker {
        margin: 28px 0 10px;
        color: #a99055;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 3px;
      }
      .portfolio-access-title { margin: 0; font-size: 30px; line-height: 1.25; }
      .portfolio-access-description {
        margin: 14px 0 30px;
        color: #aaa;
        font-size: 14px;
        line-height: 1.65;
      }
      .portfolio-access-label {
        display: block;
        margin-bottom: 9px;
        color: #ddd;
        font-size: 13px;
        font-weight: 600;
      }
      .portfolio-access-input {
        width: 100%;
        height: 56px;
        padding: 0 17px;
        border: 1px solid #3b3b3b;
        border-radius: 13px;
        outline: none;
        background: #0d0d0d;
        color: #fff;
        font-size: 16px;
        font-weight: 600;
        letter-spacing: 1px;
        text-transform: uppercase;
      }
      .portfolio-access-input:focus {
        border-color: #c7a95e;
        box-shadow: 0 0 0 3px rgba(199,169,94,.14);
      }
      .portfolio-access-input::placeholder { color: #555; }
      .portfolio-access-error {
        min-height: 20px;
        margin: 11px 0 0;
        color: #ff8f8f;
        font-size: 13px;
        line-height: 1.5;
      }
      .portfolio-access-submit {
        width: 100%;
        height: 56px;
        margin-top: 12px;
        border: 0;
        border-radius: 13px;
        background: linear-gradient(135deg, #ead083, #bd9650);
        color: #16110a;
        cursor: pointer;
        font-size: 15px;
        font-weight: 800;
      }
      .portfolio-access-status { margin: 18px 0 0; color: #aaa; font-size: 14px; }
      .portfolio-access-spinner {
        width: 32px;
        height: 32px;
        margin: 28px auto 0;
        border: 3px solid rgba(255,255,255,.14);
        border-top-color: #d9bd70;
        border-radius: 50%;
        animation: portfolio-access-spin .8s linear infinite;
      }
      @keyframes portfolio-access-spin { to { transform: rotate(360deg); } }
      @media (max-width: 520px) {
        .portfolio-access-page { padding: 16px; }
        .portfolio-access-card { padding: 38px 24px; border-radius: 22px; }
        .portfolio-access-brand { font-size: 40px; }
        .portfolio-access-title { font-size: 25px; }
      }
    </style>
    <main class="portfolio-access-page">${content}</main>
  `)
}

const renderChecking = () => {
  renderShell(`
    <section class="portfolio-access-card portfolio-access-center">
      <div class="portfolio-access-brand">MEGA</div>
      <div class="portfolio-access-spinner" aria-hidden="true"></div>
      <p class="portfolio-access-status">접속 권한을 확인하고 있습니다.</p>
    </section>
  `)
}

const removeTokenFromAddress = () => {
  const url = new URL(window.location.href)
  if (!url.searchParams.has('t')) return
  url.searchParams.delete('t')
  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

const checkToken = async portfolioToken => {
  if (!API_BASE_URL || !portfolioToken) return false

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(`${API_BASE_URL}${CHECK_TOKEN_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ portfolioToken }),
      cache: 'no-store',
      signal: controller.signal
    })
    if (!response.ok) return false
    const body = await response.json()
    return String(body.resultCode) === '0'
  } catch {
    return false
  } finally {
    clearTimeout(timeout)
  }
}

export const startPortfolioAccessGuard = ({ onGranted, onRevoked }) => {
  let activeToken = ''
  let granted = false
  let checking = false
  let pollTimer

  const stopPolling = () => {
    if (pollTimer) clearInterval(pollTimer)
    pollTimer = undefined
  }

  const renderTokenForm = (message = '') => {
    stopPolling()
    renderShell(`
      <form class="portfolio-access-card" id="portfolio-access-form">
        <div class="portfolio-access-brand">MEGA</div>
        <p class="portfolio-access-kicker">PRIVATE PORTFOLIO</p>
        <h1 class="portfolio-access-title">열람 토큰을 입력해 주세요.</h1>
        <p class="portfolio-access-description">전달받은 16자리 토큰을 입력하면 사이트를 확인할 수 있습니다.</p>
        <label class="portfolio-access-label" for="portfolio-access-token">열람 토큰</label>
        <input class="portfolio-access-input" id="portfolio-access-token" type="text"
          inputmode="text" autocomplete="off" maxlength="32"
          placeholder="예: K7M4XP9RTB2WVHQ3" />
        <p class="portfolio-access-error" id="portfolio-access-error" role="alert">${message}</p>
        <button class="portfolio-access-submit" type="submit">사이트 열기</button>
      </form>
    `)

    const form = document.querySelector('#portfolio-access-form')
    const input = document.querySelector('#portfolio-access-token')
    const error = document.querySelector('#portfolio-access-error')
    input.value = activeToken
    input.focus()

    form.addEventListener('submit', event => {
      event.preventDefault()
      const token = normalizeToken(input.value)
      if (!TOKEN_PATTERN.test(token)) {
        error.textContent = '16자리 열람 토큰을 정확히 입력해 주세요.'
        return
      }
      activeToken = token
      sessionStorage.setItem(TOKEN_STORAGE_KEY, activeToken)
      renderChecking()
      validateAccess()
    })
  }

  const startPolling = () => {
    stopPolling()
    pollTimer = setInterval(() => {
      if (!document.hidden) validateAccess()
    }, POLL_MS)
  }

  const validateAccess = async () => {
    if (checking) return
    checking = true
    const valid = await checkToken(activeToken)
    checking = false

    if (!valid) {
      if (granted) onRevoked()
      granted = false
      renderTokenForm('토큰이 유효하지 않거나 만료되었습니다. 다시 확인해 주세요.')
      return
    }

    if (!granted) {
      clearBody()
      onGranted()
      granted = true
    }
    startPolling()
  }

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopPolling()
    } else if (activeToken) {
      validateAccess()
    }
  })

  const queryToken = normalizeToken(new URLSearchParams(location.search).get('t'))
  const storedToken = normalizeToken(sessionStorage.getItem(TOKEN_STORAGE_KEY))
  activeToken = queryToken || storedToken

  if (queryToken) {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, queryToken)
    removeTokenFromAddress()
  }

  if (!TOKEN_PATTERN.test(activeToken)) {
    renderTokenForm()
    return
  }

  renderChecking()
  validateAccess()
}
